import type { Editor } from "@tiptap/react";
import { useFormikContext } from "formik";
import { lazy, type Ref, Suspense } from "react";
import { AgentSkillKind } from "@common/types/agentSkill";
import { FormikField } from "@components/forms/FormikField/FormikField";
import { Loader } from "@design-system/Loader/Loader";
import { AgentSkillFormValues, agentSkillBodyMaxChars } from "./agentSkillForm";

// Tiptap and its markdown parser load with the first skill document, not
// with the app.
const AgentSkillBodyEditor = lazy(() =>
    import("./AgentSkillBodyEditor").then((m) => ({
        default: m.AgentSkillBodyEditor,
    })),
);

interface Props {
    markdown: string;
    revisionId: string | null;
    isEditable: boolean;
    kind: AgentSkillKind;
    onLoaded?: (normalizedMarkdown: string) => void;
    editorRef?: Ref<Editor | null>;
    className?: string;
}

/** The body editor bound to the form's `body`, with the schema error under it. */
export const AgentSkillBodyField = ({
    markdown,
    revisionId,
    isEditable,
    kind,
    onLoaded,
    editorRef,
    className,
}: Props) => {
    const { values } = useFormikContext<AgentSkillFormValues>();
    const count = values.body.length.toLocaleString("en-US");
    const max = agentSkillBodyMaxChars(kind).toLocaleString("en-US");
    return (
        <FormikField<string>
            name="body"
            label="Skill text"
            labelHidden
            description={
                isEditable ? `${count} / ${max} characters` : undefined
            }
            className={className}
        >
            {({ helpers, invalid }) => (
                <Suspense fallback={<Loader />}>
                    <AgentSkillBodyEditor
                        markdown={markdown}
                        revisionId={revisionId}
                        isEditable={isEditable}
                        invalid={invalid}
                        onLoaded={onLoaded}
                        editorRef={editorRef}
                        onMarkdownChange={(body) => void helpers.setValue(body)}
                        onBlur={() => void helpers.setTouched(true)}
                    />
                </Suspense>
            )}
        </FormikField>
    );
};
